import html2pdf from 'html2pdf.js';
import jsPDF from 'jspdf';
import {
  applyReportPdfLightTheme,
  PDF_SKIP_PAINT_TAGS
} from '@/utils/reportPdfLightTheme';
import {
  collectPdfKeepBlocks,
  cssBlocksToCanvasBlocks,
  planCanvasPageSlices,
  REPORT_PDF_PAGE
} from '@/utils/reportPdfPageBreak';

/** html2canvas(구버전)이 파싱 못 하는 CSS Color 4 색(color(srgb …), color-mix 등) 대응 */
const HTML2CANVAS_COLOR_PROPERTIES = [
  'color',
  'background-color',
  'border-top-color',
  'border-right-color',
  'border-bottom-color',
  'border-left-color',
  'outline-color',
  'text-decoration-color',
  'column-rule-color',
  'fill',
  'stroke'
];

/**
 * 브라우저가 직렬화한 색을 canvas가 이해하는 rgb/#hex로 맞춘다.
 * @param {string} raw
 * @param {CanvasRenderingContext2D} ctx
 * @returns {string|null} null이면 인라인 덮어쓰기 생략
 */
function normalizeColorForHtml2Canvas(raw, ctx) {
  if (raw == null) return null;
  const v = String(raw).trim();
  if (v === '' || v === 'inherit') return null;
  if (v === 'none' || v === 'transparent') return v;
  if (/^rgba?\(/i.test(v)) return v;
  if (/^hsla?\(/i.test(v)) return v;
  if (/^#([0-9a-f]{3,8})$/i.test(v)) return v;

  try {
    ctx.fillStyle = '#000000';
    ctx.fillStyle = v;
    const out = ctx.fillStyle;
    return typeof out === 'string' && out.length ? out : '#000000';
  } catch {
    return '#000000';
  }
}

/**
 * html2canvas 클론 트리에 안전한 색만 남기도록 인라인으로 고정한다.
 * @param {HTMLElement} root
 */
function applyHtml2CanvasColorCompat(root) {
  if (!root || root.nodeType !== 1) return;

  const doc = root.ownerDocument || document;
  const win = doc.defaultView || window;
  const canvas = doc.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return;

  const walk = (el) => {
    if (!el || el.nodeType !== 1) return;
    const tag = el.tagName;
    if (tag === 'SCRIPT' || tag === 'STYLE') return;

    try {
      const cs = win.getComputedStyle(el);

      for (const prop of HTML2CANVAS_COLOR_PROPERTIES) {
        let val = cs.getPropertyValue(prop);
        if (!val || !String(val).trim()) continue;
        val = String(val).trim();
        if ((prop === 'fill' || prop === 'stroke') && val === 'none') continue;

        const safe = normalizeColorForHtml2Canvas(val, ctx);
        if (safe != null) el.style.setProperty(prop, safe, 'important');
      }

      const boxShadow = cs.getPropertyValue('box-shadow');
      if (/color\(/i.test(boxShadow) || /color-mix\(/i.test(boxShadow)) {
        el.style.setProperty('box-shadow', 'none', 'important');
      }
      const textShadow = cs.getPropertyValue('text-shadow');
      if (/color\(/i.test(textShadow) || /color-mix\(/i.test(textShadow)) {
        el.style.setProperty('text-shadow', 'none', 'important');
      }
    } catch {
      /* ignore per-node */
    }

    const { children } = el;
    for (let i = 0; i < children.length; i++) walk(children[i]);
  };

  walk(root);
}

/**
 * html2canvas 옵션에 Color 4 호환 onclone을 합친다.
 * @param {Object} html2canvasOpts
 * @returns {Object}
 */
function withHtml2CanvasColorCompatOnClone(html2canvasOpts = {}, keepBlocksRef) {
  const userOnClone = html2canvasOpts.onclone;
  const scale = Number(html2canvasOpts.scale) > 0 ? Number(html2canvasOpts.scale) : 2;
  return {
    ...html2canvasOpts,
    async onclone(a, b) {
      if (typeof userOnClone === 'function') {
        await userOnClone(a, b);
      }
      let root = b;
      if (a && a.nodeType === 1 && !a.documentElement) {
        root = a;
      } else if (a && a.documentElement) {
        root = b || a.querySelector('.html2pdf__container') || a.body || a.documentElement;
      }
      applyReportPdfLightTheme(root);
      applyHtml2CanvasColorCompat(root);
      if (keepBlocksRef && Array.isArray(keepBlocksRef)) {
        keepBlocksRef.length = 0;
        const blocks = collectPdfKeepBlocks(root, { scale });
        for (let i = 0; i < blocks.length; i++) keepBlocksRef.push(blocks[i]);
      }
    }
  };
}

/**
 * PDF 다운로드 유틸리티 클래스
 * HTML 요소를 PDF로 변환하여 다운로드하는 기능을 제공합니다.
 */
export class PdfDownloader {
  /**
   * 긴 캔버스를 블록을 자르지 않는 y구간으로 나눠 A4 PDF로 저장한다.
   * @param {HTMLCanvasElement} canvas
   * @param {{ y: number, height: number }[]} slices
   * @param {string} filename
   * @param {Object} options
   */
  static saveCanvasSlicesAsPdf(canvas, slices, filename, options = {}) {
    const jsPdfOpts = options.jsPDF || { unit: 'mm', format: 'a4', orientation: 'portrait' };
    const imageType = (options.image && options.image.type) || 'jpeg';
    const imageQuality = (options.image && options.image.quality) || 0.98;
    const pdf = new jsPDF(jsPdfOpts);
    const pageW = pdf.internal.pageSize.getWidth();
    const pageHmm = pdf.internal.pageSize.getHeight();
    const pageCanvas = document.createElement('canvas');
    const pageCtx = pageCanvas.getContext('2d');
    pageCanvas.width = canvas.width;

    const list = slices && slices.length ? slices : [{ y: 0, height: canvas.height }];
    list.forEach((slice, index) => {
      const h = Math.max(1, Math.round(slice.height));
      pageCanvas.height = h;
      pageCtx.fillStyle = '#ffffff';
      pageCtx.fillRect(0, 0, pageCanvas.width, h);
      pageCtx.drawImage(canvas, 0, slice.y, canvas.width, h, 0, 0, canvas.width, h);
      const sliceHmm = (h * pageW) / canvas.width;
      if (index > 0) pdf.addPage();
      const imgData = pageCanvas.toDataURL(`image/${imageType}`, imageQuality);
      pdf.addImage(imgData, imageType.toUpperCase(), 0, 0, pageW, sliceHmm);
    });

    const totalPages = pdf.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      if (i === 1) continue;
      pdf.setPage(i);
      pdf.setFontSize(10);
      pdf.setTextColor(100);
      pdf.text(`page ${i - 1} / ${totalPages - 1}`, pageW / 2.2, pageHmm - 5);
    }

    const outName = filename.endsWith('.pdf') ? filename : `${filename}.pdf`;
    pdf.save(outName);
    return outName;
  }

  /**
   * HTML 요소를 PDF로 변환하여 다운로드
   * @param {HTMLElement} element - PDF로 변환할 HTML 요소
   * @param {string} filename - 다운로드할 파일명 (확장자 제외)
   * @param {Object} options - PDF 생성 옵션
   */
  static async downloadAsPdf(element, filename = 'report', options = {}) {
    try {
      const defaultOptions = {
        filename: `${filename}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#fafaf4'
        },
        jsPDF: {
          unit: 'mm',
          format: 'a4',
          orientation: 'portrait'
        }
      };

      const keepBlocksRef = [];
      const pdfOptions = {
        ...defaultOptions,
        ...options,
        html2canvas: withHtml2CanvasColorCompatOnClone(
          {
            ...defaultOptions.html2canvas,
            ...(options.html2canvas || {})
          },
          keepBlocksRef
        )
      };

      console.log('[PdfDownloader] PDF 생성 시작', { filename, options: pdfOptions });

      const canvas = await new Promise((resolve, reject) => {
        html2pdf()
          .from(element)
          .set(pdfOptions)
          .toCanvas()
          .get('canvas')
          .then((c) => resolve(c), reject);
      });

      if (!canvas) {
        throw new Error('PDF canvas was not created');
      }

      const footerPx = Math.round((canvas.width * REPORT_PDF_PAGE.footerMm) / 210);
      const maxPageHeight = Math.max(1, Math.floor(canvas.width * REPORT_PDF_PAGE.a4Ratio) - footerPx);
      const canvasBlocks = cssBlocksToCanvasBlocks(keepBlocksRef, canvas.width);
      const slices = planCanvasPageSlices(canvas.height, maxPageHeight, canvasBlocks);

      const savedName = this.saveCanvasSlicesAsPdf(canvas, slices, pdfOptions.filename, pdfOptions);
      console.log('[PdfDownloader] PDF 다운로드 완료', { filename: savedName, slices: slices.length });
      return { success: true, filename: savedName };
    } catch (error) {
      console.error('[PdfDownloader] PDF 생성 실패', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * 보고서 다운로드 전용 메서드
   * @param {HTMLElement} element - 보고서 HTML 요소
   * @param {string} reportId - 보고서 ID
   * @param {string} siteName - 발전소명
   * @param {Object} fontOptions - 폰트 옵션
   */
  static async downloadReport(element, reportName, siteName, fontOptions = {}) {
    const filename = `SOLVIT_Report${siteName ? `_${siteName}` : ''}_${reportName}`;
    console.log('fontOptions', fontOptions);
    const preparedElement = await this.prepareElementForPdf(element, fontOptions);

    return await this.downloadAsPdf(preparedElement, filename, {
      pagebreak: {
        mode: [],
        before: [],
        after: [],
        avoid: []
      },
      html2canvas: {
        scale: Number(fontOptions.scale) || 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        allowTaint: true,
        scrollX: 0,
        scrollY: 0,
        letterRendering: true,
        removeContainer: true
      },
      jsPDF: {
        unit: 'mm',
        format: 'a4',
        orientation: 'portrait'
      }
    });
  }

  /**
   * PDF 생성 전 요소 준비
   * @param {HTMLElement} element - 준비할 HTML 요소
   * @param {Object} fontOptions - 폰트 옵션
   */
  static async prepareElementForPdf(element, fontOptions = {}) {
    // 기본 폰트 옵션
    const defaultFontOptions = {
      baseFontSize: '12px',
      baseFontWeight: 'normal',
      headerFontSize: '18px',
      headerFontWeight: 'bold',
      subHeaderFontSize: '14px',
      subHeaderFontWeight: 'bold',
      tableFontSize: '11px',
      tableFontWeight: 'normal',
      tableHeaderFontWeight: 'bold',
      lineHeight: '1.4',
      fontFamily: 'Arial, sans-serif'
    };

    const options = { ...defaultFontOptions, ...fontOptions };

    // 요소 복제
    const clonedElement = element.cloneNode(true);
    applyReportPdfLightTheme(clonedElement);
    console.log('calculateElementSize2', options);
    // 버튼 제거 (PDF 다운로드 버튼, X버튼 등)
    const buttons = clonedElement.querySelectorAll('button, .v-btn, .header-buttons');
    buttons?.forEach(button => {
      if (button.parentNode) {
        button.parentNode.removeChild(button);
      }
    });

    // PDF용 폰트: 화면 다크 테마 색은 유지하지 않는다(A4 인쇄는 항상 밝은 지면).
    const allElements = clonedElement.querySelectorAll('*');
    allElements?.forEach(el => {
      const tag = el.tagName;
      if (PDF_SKIP_PAINT_TAGS.has(tag)) {
        return;
      }
      if (typeof el.closest === 'function' && el.closest('svg')) {
        return;
      }
      el.style.fontFamily = options.fontFamily;
      el.style.fontSize = options.baseFontSize;
      el.style.fontWeight = options.baseFontWeight;
      el.style.lineHeight = options.lineHeight;
    });

    // 헤더 스타일 조정
    const headers = clonedElement.querySelectorAll('h1, h3');
    headers?.forEach(header => {
      if (header.tagName === 'H1') {
        header.style.fontSize = options.headerFontSize;
        header.style.fontWeight = options.headerFontWeight;
        header.style.textAlign = 'center';
      } else if (header.tagName === 'H3') {
        header.style.fontSize = options.subHeaderFontSize;
        header.style.fontWeight = options.subHeaderFontWeight;
        header.style.width = '100%';
        header.style.display = 'block';
      }
    });

    // 테이블 스타일 조정
    const tableCells = clonedElement.querySelectorAll('th, td');
    tableCells?.forEach(cell => {
      // 결함 상세 표는 컴포넌트 단에서 레이아웃/폰트 강조를 정교하게 잡았으므로
      // PDF 클론에서 전역 td/th 스타일을 덮어쓰지 않는다.
      if (typeof cell.closest === 'function' && cell.closest('.defect-detail-table')) {
        return;
      }
      cell.style.fontSize = options.tableFontSize;
      cell.style.padding = '6px';
      cell.style.fontWeight = options.tableFontWeight;
    });

    // 테이블 헤더 강조
    const tableHeaders = clonedElement.querySelectorAll('th');
    tableHeaders?.forEach(header => {
      header.style.fontWeight = options.tableHeaderFontWeight;
    });

    applyReportPdfLightTheme(clonedElement);
    clonedElement.style.width = REPORT_PDF_PAGE.contentWidthCss;
    return clonedElement;
  }


}

/**
 * PDF 다운로드 훅 (Vue 컴포넌트용)
 */
export const usePdfDownload = () => {
  const downloadReport = async (element, reportName, siteName = '', fontOptions = {}) => {
    try {
      const result = await PdfDownloader.downloadReport(element, reportName, siteName || '', fontOptions);

      if (result.success) {
        console.log('PDF 다운로드 성공:', result.filename);
      } else {
        console.error('PDF 다운로드 실패:', result.error);
        throw new Error(result.error);
      }

      return result;
    } catch (error) {
      console.error('PDF 다운로드 중 오류:', error);
      throw error;
    }
  };

  return {
    downloadReport
  };
};
